import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { Icon, Modal, SemanticCOLORS, SemanticICONS } from 'semantic-ui-react';
import { PositionedModal } from 'common/components/PositionedModal';
import { getMemberId, getSelectedAssessmentPeriodId, isCurrentMemberTeamLeader } from 'common/selectors/auth';
import LeaderboardPointsHistoryChart from './LeaderboardPointsHistoryChart';
import { useApiPortalV1AssessmentsLeaderboardEventsListQuery } from 'store/generated/portal/assessments/core';

interface TeamLeaderLeaderboardModalProps {
  selectedLeader: any | null;
  open: boolean;
  onClose: () => void;
}

type ScoreComponents = Record<string, number | undefined>;
type ScoreRowBadge = {
  icon: SemanticICONS;
  color: SemanticCOLORS;
  borderColor: string;
  backgroundColor: string;
};

type ScoreRow = {
  title: string;
  description: string;
  points: number;
  count: number;
  badge?: ScoreRowBadge;
  isDisabled?: boolean;
};

type ScoreRowDefinition = {
  metricKey: string;
  scoreKey: string;
  title: string;
  description: (components: ScoreComponents) => string;
  badge?: ScoreRowBadge;
};

const scoreRowDefinitions: ScoreRowDefinition[] = [
  {
    metricKey: 'last_login_in_period',
    scoreKey: 'last_login_in_period_score',
    title: 'Assessment Reviewed',
    description: () => 'Logged in and reviewed team assessment',
    badge: {
      icon: 'sign in alternate',
      color: 'purple',
      borderColor: '#8b5cf6',
      backgroundColor: 'rgba(139, 92, 246, 0.12)',
    },
  },
  {
    metricKey: 'focus_plan_interventions',
    scoreKey: 'focus_plan_interventions_score',
    title: 'Focus Plans Created',
    description: () => 'Selected projects to improve team culture',
    badge: {
      icon: 'clipboard list',
      color: 'green',
      borderColor: '#22c55e',
      backgroundColor: 'rgba(34, 197, 94, 0.12)',
    },
  },
  {
    metricKey: 'focus_plan_culture_actions',
    scoreKey: 'focus_plan_culture_actions_score',
    title: 'Actions Logged',
    description: () => 'Actions taken in support of focus plans',
    badge: {
      icon: 'star',
      color: 'orange',
      borderColor: '#f59e0b',
      backgroundColor: 'rgba(245, 158, 11, 0.12)',
    },
  },
  {
    metricKey: 'knowledge_base_shares',
    scoreKey: 'knowledge_base_share_score',
    title: 'Resources Shared',
    description: () => 'Shared knowledge base resources',
    badge: {
      icon: 'share',
      color: 'teal',
      borderColor: '#14b8a6',
      backgroundColor: 'rgba(20, 184, 166, 0.12)',
    },
  },
  {
    metricKey: 'video_watches',
    scoreKey: 'video_watch_score',
    title: 'Videos Watched',
    description: () => 'Video resources accessed',
    badge: {
      icon: 'play circle',
      color: 'blue',
      borderColor: '#3b82f6',
      backgroundColor: 'rgba(59, 130, 246, 0.12)',
    },
  },
  {
    metricKey: 'pdf_opens',
    scoreKey: 'pdf_open_score',
    title: 'Documents Viewed',
    description: () => 'Document resources accessed',
    badge: {
      icon: 'file pdf',
      color: 'red',
      borderColor: '#ef4444',
      backgroundColor: 'rgba(239, 68, 68, 0.12)',
    },
  },
  {
    metricKey: 'link_opens',
    scoreKey: 'link_open_score',
    title: 'Links Viewed',
    description: () => 'Link resources accessed',
    badge: {
      icon: 'external alternate',
      color: 'violet',
      borderColor: '#7c3aed',
      backgroundColor: 'rgba(124, 58, 237, 0.12)',
    },
  },
  {
    metricKey: 'file_share_views',
    scoreKey: 'file_share_view_score',
    title: 'Shared Resources Viewed',
    description: () => 'Shared files opened by recipients',
    badge: {
      icon: 'eye',
      color: 'blue',
      borderColor: '#3b82f6',
      backgroundColor: 'rgba(59, 130, 246, 0.12)',
    },
  },
];

const eventTypeScoringHelp: Record<string, string> = {
  focus_plan_intervention: 'Earn points when a focus plan is created by a team leader.',
  focus_plan_intervention_reversal: 'Remove points when a previously logged focus plan is removed or reversed.',
  focus_plan_culture_action: 'Earn points when a culture action tied to a focus plan is completed.',
  focus_plan_culture_action_reversal: 'Remove points when a previously logged culture action is removed or reversed.',
  video_watch: 'Earn points for watching a video resource relevant to team culture.',
  pdf_open: 'Earn points for opening a document resource relevant to team culture.',
  link_open: 'Earn points for clicking a URL resource relevant to team culture.',
  file_share_viewed: 'Earn points when a shared file link is opened by a recipient.',
  knowledge_base_share: 'Earn points for sharing a knowledge base resource with the team.',
  login: 'Tracks a login event. Only the first login after an assessment is completed earns points.',
  first_period_login: 'Earn bonus points for the first login after an assessment is completed.',
};

const TeamLeaderLeaderboardModal: React.FC<TeamLeaderLeaderboardModalProps> = ({ selectedLeader, open, onClose }) => {
  const selectedAssessmentPeriodId = useSelector(getSelectedAssessmentPeriodId);
  const isTeamLeader = useSelector(isCurrentMemberTeamLeader);
  const currentMemberId = useSelector(getMemberId);

  const [activeTab, setActiveTab] = useState<'accomplishments' | 'activities'>('accomplishments');

  const selectedLeaderId = selectedLeader?.leader_id ?? null;
  const isViewingOwnData = selectedLeaderId !== null && currentMemberId !== undefined && selectedLeaderId === currentMemberId;
  const shouldHideActivitiesTab = isTeamLeader && !isViewingOwnData;

  useEffect(() => {
    if (shouldHideActivitiesTab && activeTab === 'activities') {
      setActiveTab('accomplishments');
    }
  }, [shouldHideActivitiesTab, activeTab]);

  const leaderboardEventsQueryArgs = {
    limit: 200,
    offset: 0,
    ...(selectedAssessmentPeriodId ? { assessmentPeriodId: selectedAssessmentPeriodId } : {}),
  };

  const { data: leaderboardEvents, isLoading: isActivitiesLoading } = useApiPortalV1AssessmentsLeaderboardEventsListQuery(
    leaderboardEventsQueryArgs,
    {
      skip: !open || !selectedAssessmentPeriodId,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: false,
    }
  );

  const selectedLeaderRows = useMemo(() => {
    if (!selectedLeader) {
      return [] as ScoreRow[];
    }

    const scoreComponents = (selectedLeader.score_components || {}) as ScoreComponents;

    const buildScoreRow = (definition: ScoreRowDefinition): ScoreRow => {
      const countValue = scoreComponents[definition.metricKey];
      const count = typeof countValue === 'number' ? countValue : 0;
      const points = Number(scoreComponents[definition.scoreKey] || 0);

      return {
        title: definition.title,
        description: definition.description(scoreComponents),
        points,
        count,
        badge: definition.badge,
        isDisabled: count === 0,
      };
    };

    return scoreRowDefinitions.map(buildScoreRow);
  }, [selectedLeader]);

  const activityEvents = useMemo(() => {
    if (!selectedLeader || !leaderboardEvents?.results || !selectedAssessmentPeriodId) {
      return [] as Array<{
        event_date?: string | null;
        event_type: string;
        display_event_type?: string;
        event_label: string;
        points: number;
        leader?: { id: number } | null;
        assessment_period?: number;
      }>;
    }

    return ((leaderboardEvents.results || []) as Array<{
      event_date?: string | null;
      event_type: string;
      display_event_type?: string;
      event_label: string;
      points: number;
      leader?: { id: number } | null;
      assessment_period?: number;
    }>)
      .filter(event =>
        event.leader?.id === selectedLeader.leader_id &&
        event.assessment_period === selectedAssessmentPeriodId
      )
      .sort((a, b) => {
        const dateA = a.event_date ? new Date(a.event_date).getTime() : 0;
        const dateB = b.event_date ? new Date(b.event_date).getTime() : 0;
        return dateB - dateA;
      });
  }, [selectedLeader, leaderboardEvents, selectedAssessmentPeriodId]);

  const leaderName = selectedLeader ? `${selectedLeader.leader_first_name || ''} ${selectedLeader.leader_last_name || ''}`.trim() : 'Score breakdown';

  return (
    <PositionedModal open={open} onClose={onClose} size="large" closeIcon>
      <Modal.Header>{selectedLeader ? `${leaderName} - Weekly Progress` : leaderName}</Modal.Header>
      <Modal.Content>
        <ContentWrapper>
          {selectedLeader ? (
            <>
              <ChartSection>
                <LeaderboardPointsHistoryChart weeklyPointsHistory={selectedLeader.weekly_points_history} />
              </ChartSection>

              <TabsWrapper>
                <TabItem
                  active={activeTab === 'accomplishments'}
                  onClick={() => setActiveTab('accomplishments')}
                >
                  Accomplishments
                </TabItem>
                {!shouldHideActivitiesTab && (
                  <TabItem
                    active={activeTab === 'activities'}
                    onClick={() => setActiveTab('activities')}
                  >
                    Activities
                  </TabItem>
                )}
              </TabsWrapper>

              <TabContent>
                {activeTab === 'accomplishments' ? (
                  selectedLeaderRows.length > 0 ? (
                    <BreakdownTables>
                      {[selectedLeaderRows.slice(0, 4), selectedLeaderRows.slice(4)]
                        .filter(rows => rows.length > 0)
                        .map((rows, index) => (
                          <BreakdownTable key={index}>
                            <BreakdownHeader>
                              <BreakdownTextHeader>Accomplishment</BreakdownTextHeader>
                              <BreakdownSpacer />
                              <BreakdownCountHeader>Count</BreakdownCountHeader>
                              <BreakdownScoreHeader>Score</BreakdownScoreHeader>
                            </BreakdownHeader>
                            <BreakdownList>
                              {rows.map(row => (
                                <BreakdownRow key={row.title} $disabled={row.isDisabled}>
                                  <BreakdownIconCell>
                                    {row.badge ? (
                                      <BreakdownBadgeIcon
                                        color={row.isDisabled ? '#6b7280' : row.badge.color}
                                        borderColor={row.isDisabled ? '#d1d5db' : row.badge.borderColor}
                                        backgroundColor={row.isDisabled ? 'rgba(156, 163, 175, 0.12)' : row.badge.backgroundColor}
                                      >
                                        <Icon name={row.badge.icon} color={row.isDisabled ? 'grey' : row.badge.color} size="large" />
                                      </BreakdownBadgeIcon>
                                    ) : null}
                                  </BreakdownIconCell>
                                  <BreakdownText>
                                    <BreakdownLabel $disabled={row.isDisabled}>{row.title}</BreakdownLabel>
                                    <BreakdownDescription $disabled={row.isDisabled}>{row.description}</BreakdownDescription>
                                  </BreakdownText>
                                  <BreakdownCount $disabled={row.isDisabled}>{row.count ?? 0}</BreakdownCount>
                                  <BreakdownPoints $disabled={row.isDisabled}>{row.points > 0 ? `+${row.points}` : `${row.points}`}</BreakdownPoints>
                                </BreakdownRow>
                              ))}
                            </BreakdownList>
                          </BreakdownTable>
                        ))}
                    </BreakdownTables>
                  ) : (
                    <EmptyState>
                      <Icon name="ban" color="grey" />
                      <EmptyStateText>No accomplishments have been scored for this leader for the current period.</EmptyStateText>
                    </EmptyState>
                  )
                ) : (
                  <ActivitiesSection>
                    {isActivitiesLoading ? (
                      <LoadingWrapper>
                        <Icon name="spinner" loading /> Loading activities...
                      </LoadingWrapper>
                    ) : activityEvents.length === 0 ? (
                      <EmptyState>
                        <Icon name="history" color="grey" />
                        <EmptyStateText>No activity events available for this leader in the selected assessment period.</EmptyStateText>
                      </EmptyState>
                    ) : (
                      <ActivityTableWrapper>
                        <ActivityTable>
                          <thead>
                            <ActivityTableRowHeader>
                              <ActivityHeaderCell width="15%">Date</ActivityHeaderCell>
                              <ActivityHeaderCell width="18%">Event Type</ActivityHeaderCell>
                              <ActivityHeaderCell width="52%">Details</ActivityHeaderCell>
                              <ActivityHeaderCell right width="15%">Points</ActivityHeaderCell>
                            </ActivityTableRowHeader>
                          </thead>
                          <tbody>
                            {activityEvents.map((event, index) => (
                              <ActivityTableRow
                                key={`${event.event_date}-${index}`}
                                title={eventTypeScoringHelp[event.event_type] ?? undefined}
                              >
                                <ActivityCell>{event.event_date ? new Date(event.event_date).toLocaleDateString() : '—'}</ActivityCell>
                                <ActivityCell>{event.display_event_type || event.event_type}</ActivityCell>
                                <ActivityCell>{event.event_label}</ActivityCell>
                                <ActivityCell right>{event.points > 0 ? `+${event.points}` : `${event.points}`}</ActivityCell>
                              </ActivityTableRow>
                            ))}
                          </tbody>
                        </ActivityTable>
                      </ActivityTableWrapper>
                    )}
                  </ActivitiesSection>
                )}
              </TabContent>
            </>
          ) : (
            <p>No leader selected.</p>
          )}
        </ContentWrapper>
      </Modal.Content>
    </PositionedModal>
  );
};

export default TeamLeaderLeaderboardModal;

const BreakdownHeader = styled.div`
  display: grid;
  grid-template-columns: 40px minmax(0, 1fr) 72px 72px;
  padding: 8px 12px 0px;
  color: #111827;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
`;

const BreakdownTables = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  width: 100%;
  margin-bottom: 24px;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const BreakdownTable = styled.div`
  display: grid;
  gap: 10px;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 14px;
`;


const BreakdownSpacer = styled.div`
  width: 36px;
`;

const BreakdownTextHeader = styled.div`
  font-weight: 700;
`;

const BreakdownCountHeader = styled.div`
  text-align: right;
`;

const BreakdownScoreHeader = styled.div`
  text-align: right;
`;

const BreakdownCount = styled.div<{ $disabled?: boolean }>`
  font-weight: 700;
  color: ${props => (props.$disabled ? '#6b7280' : '#1f2937')};
  text-align: right;
  align-self: center;
`;

const BreakdownList = styled.div`
  display: grid;
  gap: 6px;
`;

const BreakdownRow = styled.div<{ $disabled?: boolean }>`
  display: grid;
  grid-template-columns: 40px minmax(0, 1fr) 72px 72px;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 12px;
  background: ${props => (props.$disabled ? '#f3f4f6' : '#ffffff')};
  border: 1px solid ${props => (props.$disabled ? 'transparent' : '#e5e7eb')};
  color: ${props => (props.$disabled ? '#6b7280' : '#111827')};
  align-items: center;
`;

const BreakdownIconCell = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const BreakdownText = styled.div`
  display: grid;
  gap: 0px;
`;

const BreakdownLabel = styled.div<{ $disabled?: boolean }>`
  font-weight: 600;
  color: ${props => (props.$disabled ? '#4b5563' : '#1f2937')};
`;

const BreakdownDescription = styled.div<{ $disabled?: boolean }>`
  font-size: 11px;
  color: ${props => (props.$disabled ? '#6b7280' : '#6b7280')};
`;

const BreakdownBadgeIcon = styled.span<{ color: string; borderColor: string; backgroundColor: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 12px;
  border: 1px solid ${props => props.borderColor};
  background: ${props => props.backgroundColor};
  color: ${props => props.color};

  i.icon,
  svg {
    width: 20px;
    height: 20px;
    font-size: 20px;
    margin-left: 0;
  }
`;

const BreakdownPoints = styled.div<{ $disabled?: boolean }>`
  font-weight: 700;
  color: ${props => (props.$disabled ? '#6b7280' : '#111827')};
  align-self: center;
  text-align: right;
  white-space: nowrap;
`;

const ChartSection = styled.div`
  margin-bottom: 24px;
`;

const TabsWrapper = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
  border-bottom: 1px solid #e5e7eb;
`;

const TabItem = styled.button<{ active: boolean }>`
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 12px 16px;
  font-size: 14px;
  font-weight: ${props => (props.active ? 700 : 500)};
  color: ${props => (props.active ? '#111827' : '#6b7280')};
  border-bottom: 3px solid ${props => (props.active ? '#2563eb' : 'transparent')};
  transition: color 0.2s ease, border-bottom-color 0.2s ease;

  &:hover {
    color: #111827;
  }
`;

const TabContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const ActivitiesSection = styled.div`
  width: 100%;
`;

const ActivityTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 100%;
  table-layout: fixed;

  thead th {
    position: sticky;
    top: 0;
    z-index: 1;
    background: #f8fafc;
  }
`;

const ActivityTableWrapper = styled.div`
  width: 100%;
  max-height: 42vh;
  min-height: 240px;
  overflow-y: auto;
  overflow-x: hidden;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  background: #ffffff;
`;

const ActivityTableRowHeader = styled.tr`
  background: transparent;
`;

const ActivityHeaderCell = styled.th<{ right?: boolean; width?: string }>`
  padding: 12px 14px;
  width: ${props => props.width || 'auto'};
  text-align: ${props => (props.right ? 'right' : 'left')};
  font-size: 12px;
  font-weight: 700;
  color: #111827;
  border-bottom: 1px solid #e5e7eb;
  background: #f8fafc;
`;

const ActivityTableRow = styled.tr`
  cursor: default;
  transition: background-color 0.2s ease;

  &:nth-child(even) {
    background: #fbfbfb;
  }

  &:hover {
    background: #eef2ff;
  }
`;

const ActivityCell = styled.td<{ right?: boolean; width?: string }>`
  padding: 12px 14px;
  width: ${props => props.width || 'auto'};
  text-align: ${props => (props.right ? 'right' : 'left')};
  color: #374151;
  font-size: 13px;
  border-bottom: 1px solid #e5e7eb;
  white-space: normal;
  word-break: break-word;
  cursor: inherit;
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 32px 18px;
  border: 1px dashed #d1d5db;
  border-radius: 14px;
  color: #6b7280;
`;

const EmptyStateText = styled.div`
  font-size: 14px;
`;

const LoadingWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 24px;
  color: #374151;
  font-size: 14px;
`;

const ContentWrapper = styled.div`
  padding: 0 24px;
`;
